import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Play, SkipForward, Dumbbell, HelpCircle } from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useTour } from '../../context/TourContext';

const OnboardingTutorial = React.memo(({ isOpen, onClose, user, profile, isManualTrigger = false }) => {
    const { t } = useTranslation();
    const { startTour } = useTour();

    const handleStart = useCallback(() => {
        startTour();
        onClose();
    }, [startTour, onClose]);

    const handleSkip = useCallback(async () => {
        onClose();
        if (user && !isManualTrigger) {
            try {
                await updateDoc(doc(db, 'users', user.uid), {
                    tutorialCompleted: true
                });
                localStorage.setItem('tutorialCompleted', 'true');
            } catch (error) {
                console.error("Error skipping tutorial:", error);
            }
        }
    }, [user, isManualTrigger, onClose]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-neutral-900 border border-neutral-800 rounded-2xl w-full max-w-md shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
                <div className="bg-gradient-to-br from-red-900/20 to-neutral-900 p-8 flex flex-col items-center justify-center text-center">
                    <div className="bg-red-600/20 p-4 rounded-full mb-4 ring-2 ring-red-600/50 shadow-[0_0_20px_rgba(220,38,38,0.3)]">
                        <Dumbbell size={40} className="text-red-500" />
                    </div>
                    <h2 className="text-2xl font-black text-white uppercase italic tracking-tight mb-2">
                        {t('tutorial.welcome_title')}
                    </h2>
                    <p className="text-neutral-400 text-sm font-medium">
                        {t('tutorial.welcome_subtitle')}
                    </p>
                </div>

                <div className="p-6 space-y-3 bg-neutral-950/50">
                    <button
                        onClick={handleStart}
                        className="w-full py-4 bg-red-600 hover:bg-red-500 text-white font-black uppercase tracking-widest rounded-xl transition-all shadow-lg active:scale-95 flex items-center justify-center gap-3"
                    >
                        <Play size={20} fill="currentColor" />
                        {t('tutorial.start_tour')}
                    </button>

                    <button
                        onClick={handleSkip}
                        className="w-full py-3 bg-transparent hover:bg-neutral-800 text-neutral-500 hover:text-white font-bold uppercase tracking-wider rounded-xl transition-colors text-xs flex items-center justify-center gap-2"
                    >
                        <SkipForward size={16} />
                        {t('tutorial.skip_tour')}
                    </button>

                    {/* Hint about repeating the tutorial */}
                    <div className="pt-3 border-t border-neutral-800 mt-3">
                        <p className="text-neutral-500 text-xs text-center flex items-center justify-center gap-1.5">
                            <HelpCircle size={12} className="text-neutral-600" />
                            {t('tutorial.restart_hint')}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default OnboardingTutorial;
